// Phase 2 (Codex code review #11): what the DE page says about running on the company's Hermes
// container, from the profile's `aigentpanel` block. It shows four statuses: the container
// (not imported, another DE's, key unconfirmed, retired, confirmed), the panel reconciliation
// (outcome unknown, late result), the routing evidence (unverified, probe passed, invalidated)
// and the execution mode. It explains the rolling LiteLLM budget, and the execution opt-in is a
// visible control behind a dialog that states F7: Shape A has no task, file or KPI tools, and
// uses the profile's own model key instead of a per-run key. Managers switch it with
// `PATCH /digital-employees/{id} {"execution": "hermes" | "local"}`; the server keeps every
// rule (bound, owner, not manual), and the page shows its refusal.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  SettingsSection,
  toast,
  workerName,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ServerIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import { formatDate } from "../format";
import { keys } from "../queries";
import { problemText } from "./de-status";

export type PanelBlock = NonNullable<DeProfile["aigentpanel"]>;
type Tone = "neutral" | "success" | "warning" | "danger" | "info";
export type Status = { state: string; label: string; detail: string | null; tone: Tone };

/** The company's container as this DE sees it. */
export function containerStatus(panel: PanelBlock): Status {
  const c = panel.container;
  if (!c)
    return {
      state: "none",
      label: "No container yet",
      detail: "The first deploy reserves the company's container for this DE.",
      tone: "neutral",
    };
  if (c.key_state === "not_imported")
    return { state: "not_imported", label: "Not imported", detail: c.message, tone: "danger" };
  if (!c.owner)
    return {
      state: "owned",
      label: "Another DE's",
      detail:
        c.message ??
        "The company's Hermes container belongs to another digital employee (one DE per container).",
      tone: "danger",
    };
  if (c.key_state === "retired")
    return { state: "retired", label: "Key retired", detail: c.message, tone: "danger" };
  if (c.key_state === "proposed")
    return {
      state: "unconfirmed",
      label: "Key unconfirmed",
      detail: "The container key is confirmed by the first passed probe.",
      tone: "warning",
    };
  return { state: "live", label: "Confirmed", detail: null, tone: "success" };
}

const RECONCILE = "panel outcome unknown: reconciliation pending";
const LATE = "panel reported a late result; probe required";

/** The panel reconciliation of the DE's newest deploy (from `problem`). */
export function reconcileStatus(panel: PanelBlock): Status | null {
  if (panel.problem === RECONCILE)
    return {
      state: "reconcile_needed",
      label: "Reconciliation pending",
      detail:
        "The panel's answer to the last deploy was lost. CompanyOS keeps its credentials and asks the panel for 24 hours; an operator settles it after that.",
      tone: "warning",
    };
  if (panel.problem === LATE)
    return {
      state: "late_result",
      label: "Late panel result",
      detail: "The panel applied an older deploy late. A new probe is required.",
      tone: "warning",
    };
  if (panel.problem)
    return { state: "problem", label: "Needs attention", detail: panel.problem, tone: "danger" };
  return null;
}

/** The routing evidence: whether a probe proved this DE's route to its container. */
export function routingStatus(panel: PanelBlock): Status {
  const e = panel.execution;
  const evidence = e?.evidence ?? (e?.routable ? "verified" : "unverified");
  if (evidence === "verified")
    return {
      state: "verified",
      label: "Probe passed",
      detail: e?.verified_at ? `Verified ${formatDate(e.verified_at.slice(0, 10))}.` : null,
      tone: "success",
    };
  if (evidence === "invalidated")
    return {
      state: "invalidated",
      label: "Invalidated",
      detail:
        "A probe passed, but its evidence is gone (a redeploy, a late panel result, drift or an operator). Runs on Hermes are refused until a new probe passes.",
      tone: "danger",
    };
  return {
    state: "unverified",
    label: "Unverified",
    detail: "No probe has passed. Runs on Hermes are refused until an operator's probe passes.",
    tone: "warning",
  };
}

/** What switching to Hermes means today (F7), in the dialog and under the control. */
export const F7_POINTS = [
  "The Hermes profile holds no CompanyOS API key, so the task, file and KPI tools and outcomes.record are unavailable to the agent. Results are collected from the run.",
  "The model calls use the profile's own LiteLLM key, not a per-run key. The run's reservation still stops the run.",
] as const;

export const ROLLING_BUDGET =
  "The panel's LiteLLM key carries the seat budget as a ceiling on a rolling window (1, 7 or 31 days) counted from the key's creation, not on CompanyOS's calendar day, week or month. CompanyOS's calendar budget, its reservation and the budget stop stay the authority.";

function Row({ label, status, slot }: { label: string; status: Status; slot: string }) {
  return (
    <div
      className="flex flex-col gap-1 border-border border-b py-2.5 last:border-b-0"
      data-slot={slot}
      data-state={status.state}
    >
      <div className="flex items-center justify-between gap-3">
        <dt className="text-sm text-muted-foreground">{label}</dt>
        <dd>
          <Badge variant={status.tone} data-dot="">
            {status.label}
          </Badge>
        </dd>
      </div>
      {status.detail ? <p className="text-xs text-muted-foreground">{status.detail}</p> : null}
    </div>
  );
}

/** Why the opt-in cannot be offered now (the server refuses it too), or null. */
function blockedReason(panel: PanelBlock, source: DeProfile["runtime_ref_source"]): string | null {
  if (!panel.execution?.bound) return "Deploy this DE on AiGentPanel first.";
  const c = panel.container;
  if (c && (!c.owner || c.key_state === "not_imported"))
    return "The company's container is not this DE's.";
  if (c?.key_state === "retired") return "The container key was retired.";
  if (source === "manual") return "A runtime set by hand is kept; clear it first.";
  return null;
}

/** The section on the DE page (Configuration › Identity & runtime). */
export function HermesExecutionSection({
  api,
  companyId,
  de,
  manager,
}: {
  api: Api;
  companyId: string;
  de: DeProfile;
  manager: boolean;
}) {
  const panel = de.aigentpanel;
  const qc = useQueryClient();
  const [asking, setAsking] = useState<"hermes" | "local" | null>(null);
  const save = useMutation({
    mutationFn: (execution: "hermes" | "local") =>
      api.client.digitalEmployees.update({
        params: { co: companyId, id: de.membership_id },
        body: { execution },
      }),
    onSuccess: (profile, execution) => {
      qc.setQueryData(keys.de(companyId, de.membership_id), profile);
      void qc.invalidateQueries({ queryKey: keys.digitalEmployees(companyId) });
      setAsking(null);
      toast.add({
        title:
          execution === "hermes"
            ? `${workerName(profile.display_name)} runs on Hermes once a probe passes`
            : `${workerName(profile.display_name)} runs locally`,
      });
    },
  });
  if (!panel) return null;
  const mode = panel.execution?.mode ?? "local";
  const blocked = mode === "local" ? blockedReason(panel, de.runtime_ref_source) : null;
  const reconcile = reconcileStatus(panel);
  const executionStatus: Status =
    mode === "hermes"
      ? {
          state: "hermes",
          label: "Hermes",
          detail: panel.execution?.message ?? null,
          tone: panel.execution?.routable ? "success" : "warning",
        }
      : {
          state: "local",
          label: "Local",
          detail: panel.execution?.message ?? "Runs on the CompanyOS runtime.",
          tone: "neutral",
        };
  return (
    <SettingsSection
      icon={ServerIcon}
      title="Hermes execution"
      description="Whether this DE runs on the company's Hermes container on AiGentPanel, and what has been proved about the route."
      data-slot="de-hermes-execution"
      actions={
        manager ? (
          mode === "local" ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={blocked !== null || save.isPending}
              title={blocked ?? undefined}
              onClick={() => {
                save.reset();
                setAsking("hermes");
              }}
            >
              Run on Hermes
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={save.isPending}
              onClick={() => {
                save.reset();
                setAsking("local");
              }}
            >
              Run locally
            </Button>
          )
        ) : null
      }
    >
      <dl className="flex flex-col">
        <Row label="Execution" status={executionStatus} slot="de-hermes-mode" />
        <Row label="Container" status={containerStatus(panel)} slot="de-hermes-container" />
        <Row label="Routing evidence" status={routingStatus(panel)} slot="de-hermes-routing" />
        {reconcile ? (
          <Row label="Panel reconciliation" status={reconcile} slot="de-hermes-reconcile" />
        ) : null}
      </dl>
      {blocked && manager ? (
        <p className="text-xs text-muted-foreground" data-slot="de-hermes-blocked">
          {blocked}
        </p>
      ) : null}
      <div className="flex flex-col gap-1.5 text-xs text-muted-foreground" data-slot="de-hermes-f7">
        <p className="font-medium text-foreground">On Hermes (Shape A) today</p>
        <ul className="list-disc pl-4">
          {F7_POINTS.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      </div>
      {panel.deployed ? (
        <p className="text-xs text-muted-foreground" data-slot="de-hermes-budget">
          {ROLLING_BUDGET}
        </p>
      ) : null}
      <AlertDialog
        open={asking !== null}
        onOpenChange={(open) => {
          if (!open && !save.isPending) setAsking(null);
        }}
      >
        <AlertDialogContent size="sm">
          {asking ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {asking === "hermes"
                    ? `Run ${workerName(de.display_name)} on Hermes?`
                    : `Run ${workerName(de.display_name)} locally?`}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {asking === "hermes"
                    ? "Runs go to the company's Hermes container once an operator's probe has passed; until then they are refused before anything is sent."
                    : "Runs go back to the CompanyOS runtime. The DE stays local: a later deploy does not switch it back."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              {asking === "hermes" ? (
                <ul
                  className="list-disc pl-4 text-sm text-muted-foreground"
                  data-slot="de-hermes-optin-f7"
                >
                  {F7_POINTS.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              ) : null}
              {save.isError ? (
                <p role="alert" className="text-sm text-danger-text">
                  {problemText(save.error, "The execution could not be changed.")}
                </p>
              ) : null}
              <AlertDialogFooter>
                <AlertDialogCancel disabled={save.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={save.isPending}
                  onClick={(e) => {
                    e.preventDefault();
                    save.mutate(asking);
                  }}
                >
                  {save.isPending
                    ? "Saving…"
                    : asking === "hermes"
                      ? "Turn on Hermes execution"
                      : "Run locally"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : null}
        </AlertDialogContent>
      </AlertDialog>
    </SettingsSection>
  );
}
